export type AdminSidebarTheme = 'dark' | 'light'
export type AdminHeaderTheme = 'primary' | 'light' | 'dark'
export type AdminTabsTheme = 'default' | 'card'

export interface AdminThemeSettings {
  primaryColor: string
  sidebar: AdminSidebarTheme
  header: AdminHeaderTheme
  tabs: AdminTabsTheme
}

export const ADMIN_THEME_STORAGE_KEY = 'kesai:admin:theme'

export const ADMIN_THEME_COLORS = [
  '#409EFF', '#0BB2D4', '#3E8EF7', '#11C26D', '#17B3A3',
  '#667AFA', '#9463F7', '#EB6709', '#F74584', '#FF4C52'
] as const

export const DEFAULT_ADMIN_THEME: AdminThemeSettings = {
  primaryColor: '#17B3A3',
  sidebar: 'dark',
  header: 'primary',
  tabs: 'default'
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export const normalizeAdminTheme = (value: unknown): AdminThemeSettings => {
  if (!isRecord(value)) return { ...DEFAULT_ADMIN_THEME }

  return {
    primaryColor: typeof value.primaryColor === 'string' &&
      ADMIN_THEME_COLORS.includes(value.primaryColor.toUpperCase() as typeof ADMIN_THEME_COLORS[number])
      ? value.primaryColor.toUpperCase()
      : DEFAULT_ADMIN_THEME.primaryColor,
    sidebar: value.sidebar === 'light' ? 'light' : DEFAULT_ADMIN_THEME.sidebar,
    header: value.header === 'light' || value.header === 'dark'
      ? value.header
      : DEFAULT_ADMIN_THEME.header,
    tabs: value.tabs === 'card' ? 'card' : DEFAULT_ADMIN_THEME.tabs
  }
}

export const readAdminTheme = (storage?: Pick<Storage, 'getItem'> | null): AdminThemeSettings => {
  const target = storage === undefined
    ? (typeof localStorage === 'undefined' ? null : localStorage)
    : storage
  const stored = target?.getItem(ADMIN_THEME_STORAGE_KEY)
  if (!stored) return { ...DEFAULT_ADMIN_THEME }

  try {
    return normalizeAdminTheme(JSON.parse(stored) as unknown)
  } catch {
    return { ...DEFAULT_ADMIN_THEME }
  }
}

export const writeAdminTheme = (
  settings: AdminThemeSettings,
  storage?: Pick<Storage, 'setItem'> | null
): void => {
  const target = storage === undefined
    ? (typeof localStorage === 'undefined' ? null : localStorage)
    : storage
  target?.setItem(ADMIN_THEME_STORAGE_KEY, JSON.stringify(normalizeAdminTheme(settings)))
}
